import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "@/lib/toast";
import { Shirt, Loader2 } from "lucide-react";
import { explodeSwagItems } from "@/lib/swag";
import { useCart, CartLink } from "@/context/CartContext";
import { PreorderRibbon } from "@/components/PreorderRibbon";

const fmtPrice = (c) =>
  c == null ? "Price soon" : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function stockNote(it) {
  if (it.stock == null) return null;
  if (it.stock <= 0) return "Sold out";
  if (it.stock <= 5) return `Only ${it.stock} left`;
  return "In stock";
}

// One storefront card = one color of an item (or a plain item). The whole card links to that item's
// dedicated product page (/swag/<slug>) — no modal.
function SwagCard({ card }) {
  const it = card.item;
  const soldOut = !it.in_stock;
  const forSale = it.price_cents != null && it.price_cents > 0;
  const note = stockNote(it);
  return (
    <Link
      to={`/swag/${card.slug}`}
      data-testid={`swag-card-${card.slug}`}
      className="group border border-sniper bg-surface overflow-hidden flex flex-col transition-colors hover:border-olive focus:outline-none focus-visible:border-olive-bright"
    >
      <div className="aspect-square bg-obsidian overflow-hidden flex items-center justify-center relative">
        {card.image ? (
          <img
            src={card.image}
            alt={card.title}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
            data-testid={`swag-image-${card.slug}`}
          />
        ) : (
          <Shirt className="w-10 h-10 text-muted-foreground" />
        )}
        {soldOut && (
          <span className="absolute top-2 left-2 font-mono text-[10px] uppercase tracking-wide bg-obsidian/80 border border-blaze/60 text-blaze px-2 py-1" data-testid={`swag-soldout-${card.slug}`}>
            Sold out
          </span>
        )}
        {it.preorder && !soldOut && (
          <PreorderRibbon size="sm" testid={`swag-preorder-${card.slug}`} />
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <span className="font-heading uppercase tracking-tight text-foreground text-lg leading-tight">{card.title}</span>
          <span className="shrink-0 font-heading text-olive-bright text-lg" data-testid={`swag-price-${card.slug}`}>{fmtPrice(it.price_cents)}</span>
        </div>
        <div className="mt-1 flex items-center gap-2">
          {it.category && <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{it.category}</span>}
          {note && !soldOut && <span className="font-mono text-[10px] uppercase tracking-wide text-secondary-foreground">· {note}</span>}
        </div>
        {it.description && (
          <p className="font-body text-[13px] text-secondary-foreground mt-2 leading-relaxed line-clamp-2">{it.description}</p>
        )}
        <span className="mt-4 inline-flex items-center gap-1.5 font-mono uppercase text-[11px] tracking-wide text-olive-bright">
          {soldOut ? "Sold out" : !forSale ? "Details" : it.preorder ? "View & pre-order" : "View & buy"} →
        </span>
      </div>
    </Link>
  );
}

export default function Swag() {
  const [items, setItems] = useState(null); // null = loading
  const [params, setParams] = useSearchParams();
  const cart = useCart();

  const load = () => api.get("/swag").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  // Return from Stripe Checkout (product page sends the buyer here on success).
  useEffect(() => {
    const order = params.get("order");
    if (!order) return;
    if (order === "success") {
      const sid = params.get("session_id");
      const finish = () => { cart?.clear(); setParams({}, { replace: true }); load(); };
      if (sid) {
        api.get(`/swag/orders/status/${sid}`)
          .then(({ data }) => {
            toast.success(data.status === "completed" ? "Order placed — thanks! Check your email for confirmation." : "Payment received — we're confirming your order.");
          })
          .catch(() => toast.success("Order placed — thanks!"))
          .finally(finish);
      } else { toast.success("Order placed — thanks!"); finish(); }
    } else if (order === "cancel") {
      toast("Checkout canceled.");
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const loading = items === null;
  const cards = items ? explodeSwagItems(items) : [];
  const hasItems = !loading && cards.length > 0;

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 py-16" data-testid="swag-page">
        <header className="mb-10">
          <div className="flex items-start justify-between gap-4">
            <p className="font-mono uppercase tracking-[0.2em] text-olive-bright text-[11px] mb-2">// The OpticSwag Drop</p>
            <CartLink />
          </div>
          <h1 className="font-heading uppercase tracking-tight text-4xl sm:text-5xl text-foreground">Swag</h1>
          <p className="font-body text-secondary-foreground mt-3 max-w-xl text-sm">
            Wear the glass. First-party gear shipped by OpticSwap — not a member sale.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground font-mono text-sm uppercase" data-testid="swag-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : !hasItems ? (
          <div className="py-28 flex flex-col items-center justify-center text-center" data-testid="swag-empty">
            <Shirt className="w-9 h-9 text-muted-foreground mb-4" />
            <p className="font-mono uppercase tracking-[0.14em] text-secondary-foreground text-base" data-testid="swag-coming">
              Swag is coming.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="swag-grid">
            {cards.map((c) => <SwagCard key={c.key} card={c} />)}
          </div>
        )}
      </main>
    </div>
  );
}
